// FEAT-C39: shared client gate for hiding confirmed+low internet coverage
// points on the admin mapping surfaces (address processing + activation
// processing), mirroring the monorepo MappedProvidersList. SHOW_MAPPING_CONFIDENCE
// is a runtime env var injected into client state (see makeInitialState `env`);
// surfaces read it with useBranch(['env', 'SHOW_MAPPING_CONFIDENCE']) and pass
// the value here. When off, every surface behaves exactly as before. Hidden
// rows stay in the browser so the reveal toggle can show them without re-fetching.
export const isShowMappingConfidenceEnabled = (value) => value === 'true';

export const isLowConfidenceProvider = (provider) =>
  provider?.coveragePoint?.confidenceStatus === 'confirmed' &&
  provider?.coveragePoint?.confidenceLevel === 'low';

// Given a provider list and whether confidence filtering is active + revealed,
// return the count of low-confidence providers and the list to display. Each
// surface decides `active` (flag + utility-type gate + role/partner-flag rules)
// and `showLowConfidence` (whether the viewer has revealed them); the count and
// filtering rule live here so they are not re-derived per component.
export const getLowConfidenceView = (providers, { active, showLowConfidence } = {}) => {
  const lowConfidenceCount = active ? providers.filter(isLowConfidenceProvider).length : 0;
  const displayedProviders =
    active && !showLowConfidence
      ? providers.filter((provider) => !isLowConfidenceProvider(provider))
      : providers;
  return { lowConfidenceCount, displayedProviders };
};

// Label for the reveal/hide toggle, shared so the pluralization stays identical.
export const lowConfidenceToggleLabel = (lowConfidenceCount, showLowConfidence) =>
  showLowConfidence
    ? 'Hide low-confidence providers'
    : `Show ${lowConfidenceCount} low-confidence provider${lowConfidenceCount === 1 ? '' : 's'}`;

// FEAT-D40: exclusivity helpers — mirror the getLowConfidenceView pattern above.
export const isExclusiveProvider = (provider) => provider?.coveragePoint?.isExclusive === true;

// FEAT-E56: mapped here, but the renter cannot buy anything from it — an internet provider
// with no visible plan and no live plan variant at this address. `countsAsMapped` is computed
// by the monorepo (providerCountsAsMapped SQL) and rides along on the coverage and activation
// provider payloads; it is TRUE for every non-internet utility type, so no type gate is needed
// here. Marked rather than hidden: hiding it would make the address look unmapped and invite a
// second coverage point for a provider that is already there.
//
// Strictly `=== false`. An undefined flag means the payload predates this field or came from an
// endpoint that does not compute it — "not evaluated", which must not render as "no plans".
export const hasNothingToSellProvider = (provider) => provider?.countsAsMapped === false;

// Suffix appended to a provider's label on the admin mapping surfaces. Additive rather than
// part of the Exclusive/low-confidence chain: the monorepo's MappedProvidersList shows those
// badges alongside each other, and an exclusive provider with nothing to sell is exactly the
// case a mapper most needs both halves of.
export const noPlansLabelSuffix = (provider) =>
  hasNothingToSellProvider(provider) ? ' (No plans)' : '';

// Providers that count toward "is this utility mapped?". Mirrors the monorepo, where
// recalculateActivationStatuses counts only `countsAsMapped !== false` and the coverage view's
// isMapped reads the same flag — so a UPT status chip cannot say an address is done while the
// Mapping Hitlist has it flagged as missing that utility. Use this instead of a bare
// `availableProviders.length` wherever the answer drives a mapped/unmapped status.
export const countsAsMappedProviders = (providers = []) =>
  providers.filter((provider) => !hasNothingToSellProvider(provider));

// Per utility-type provider list: if any provider is exclusive, keep ONLY exclusive ones.
export const getExclusiveView = (providers, { active } = {}) => {
  if (!active) return { exclusiveCount: 0, displayedProviders: providers };
  const exclusive = providers.filter(isExclusiveProvider);
  if (exclusive.length === 0) return { exclusiveCount: 0, displayedProviders: providers };
  return { exclusiveCount: providers.length - exclusive.length, displayedProviders: exclusive };
};

// FEAT-D40: single source of truth for what a surface displays per utility type.
// Exclusivity SUPERSEDES confidence: if any provider is exclusive, show ONLY the
// exclusive one(s) and suppress all low-confidence handling (no "Low confidence"
// label, no reveal toggle) — exclusivity is checked on the FULL list first, so a
// low-confidence exclusive provider is still shown. With no exclusive provider,
// behavior is the unchanged FEAT-C39 low-confidence view.
export const getProviderDisplayView = (providers, { confidenceActive, showLowConfidence } = {}) => {
  const exclusive = providers.filter(isExclusiveProvider);
  if (exclusive.length > 0) {
    return { hasExclusive: true, displayedProviders: exclusive, lowConfidenceCount: 0 };
  }
  const { lowConfidenceCount, displayedProviders } = getLowConfidenceView(providers, {
    active: confidenceActive,
    showLowConfidence,
  });
  return { hasExclusive: false, displayedProviders, lowConfidenceCount };
};
